import type { OptionsDocument } from 'paintscout'

export interface CompanyOptionsValues {
  name: string
  address: string
  phone: string
  additional: string
  email: string
  postal: string
  city: string
  country: string
  province: string
  dateFormat: string
  timezone: string
  brand: {
    logo: string
    primaryColor: string
    secondaryColor: string
  }
  integrations: {
    pipelineDeals?: {
      apiKey: string
    }
  }
  hourlyRate: {
    value: number
    allowCustom: boolean
  }
  subTotal: {
    allowCustom: boolean
  }
  taxes: {
    description: string
    value: number
    allowCustom: boolean
  }
  discount: {
    description: string
    value: number
    type: string
    show: boolean
    allowCustom: boolean
  }
  serviceFees?: {
    allowCustom?: boolean
    hiddenValue?: number
    visibleValue?: number
  }
  pdf: {
    letterhead: boolean
    filenameFormat: string
  }
  labels?: {
    quote?: string
    workOrder?: string
    application?: string
    labour?: string
    prep?: string
  }
  acceptDisclaimer: string
  declineDisclaimer: string
  allowDecline: boolean
}

export function getCompanyOptions(args: { options: OptionsDocument }): CompanyOptionsValues {
  const { options } = args.options

  const integrations: CompanyOptionsValues['integrations'] = {}

  if (options.pipelineKey) {
    integrations.pipelineDeals = {
      apiKey: options.pipelineKey.value
    }
  }

  return {
    name: options.companyName.value as string,
    address: options.companyAddress.value as string,
    phone: options.companyPhone.value as string,
    additional: (options.companyAdditional?.value ?? '') as string,
    email: options.companyEmail ? (options.companyEmail.value as string) : undefined,
    postal: options.companyPostal.value as string,
    city: options.companyCity.value as string,
    country: options.companyCountry.value as string,
    province: options.companyProv.value as string,
    dateFormat: options.dateFormat.momentValue,
    timezone: options.timezone,
    brand: {
      logo: options.logo.value,
      primaryColor: options.primaryColor.value as string,
      secondaryColor: options.secondaryColor.value as string
    },
    integrations,
    hourlyRate: {
      value: options.hourlyRate.value as number,
      allowCustom: !!(options.hourlyRate && options.hourlyRate.allow_custom) as boolean
    },
    subTotal: {
      allowCustom: !!(options.subTotal && options.subTotal.allow_custom) as boolean
    },
    taxes: {
      description: options.taxRate.description as string,
      value: options.taxRate.value as number,
      allowCustom: !!(options.taxRate && options.taxRate.allow_custom) as boolean
    },
    discount: {
      description:
        options.discountDescription && options.discountDescription.value
          ? (options.discountDescription.value as string)
          : ('' as string),
      type: (options.discountType && options.discountType.value ? options.discountType.value : 'percentage') as string,
      value:
        options.discountRate && options.discountRate.value ? (options.discountRate.value as number) : (0 as number),
      show: !!(options.discountShow && options.discountShow.value) as boolean,
      allowCustom: !!(options.discountRate && options.discountRate.allow_custom) as boolean
    },
    serviceFees: {
      allowCustom: !!(options.quotes?.serviceFees && options.quotes.serviceFees.allow_custom) as boolean,
      hiddenValue:
        options.quotes?.serviceFees && options.quotes.serviceFees.hiddenValue
          ? options.quotes.serviceFees.hiddenValue
          : 0,
      visibleValue:
        options.quotes?.serviceFees && options.quotes.serviceFees?.visibleValue
          ? options.quotes.serviceFees.visibleValue
          : 0
    },
    pdf: {
      letterhead: !!(options.pdf && options.pdf.letterhead && options.pdf.letterhead.enabled) as boolean,
      filenameFormat:
        options.pdf && options.pdf.filename && options.pdf.filename.format ? options.pdf.filename.format : ''
    },
    acceptDisclaimer: options.acceptDisclaimer ? options.acceptDisclaimer : '',
    declineDisclaimer: options.declineDisclaimer ? options.declineDisclaimer : '',
    allowDecline: options.allowDecline ? options.allowDecline : false,
    labels: options.labels
      ? options.labels
      : {
          quote: 'Quote',
          workOrder: 'Work Order',
          application: 'Coat',
          labour: 'Painting',
          prep: 'Prep'
        }
  }
}
